import { Link } from '@tanstack/react-router'
import { Alert, Descriptions, Table, Typography } from 'antd'
import { ZoneParams } from 'features/libra/stepComponents/zoneAnalysis/zoneAnalysisApi'
import { createLibraSearch } from 'routes/libra/-searchParams'

import { NiiAssessmentReference } from './planningTypes'

const buildColumns = (coverageByZone: Record<string, number | undefined>) => [
  { title: 'NII zone', dataIndex: 'title' },
  {
    title: 'Damage mechanisms',
    render: (_: unknown, zone: ZoneParams) =>
      zone.damage_mechanisms
        ?.map((mechanism) => mechanism.name)
        .filter(Boolean)
        .join('; ') || 'Not specified',
  },
  {
    title: 'Strategy',
    render: (_: unknown, zone: ZoneParams) =>
      zone.nii_strategy?.strategy_type_override ??
      zone.nii_strategy?.strategy_type_calculated ??
      'Not specified',
  },
  {
    title: 'Required coverage',
    render: (_: unknown, zone: ZoneParams) => {
      const coverage = coverageByZone[zone.id]
      return coverage === undefined ? 'Not specified' : `${coverage}%`
    },
  },
  {
    title: 'NII recommendations',
    render: (_: unknown, zone: ZoneParams) =>
      zone.zone_recommendations?.join('; ') || 'Not specified',
  },
]

export const NiiAssessmentBasisCard = ({
  source,
  zones,
  organizationId,
  coverageByZone,
}: {
  coverageByZone: Record<string, number | undefined>
  organizationId?: string
  source: NiiAssessmentReference
  zones: ZoneParams[]
}) => (
  <>
    <Typography.Title level={4}>NII assessment basis</Typography.Title>
    <Descriptions
      column={1}
      bordered
      items={[
        {
          key: 'source',
          label: 'Completed NII ticket',
          children: (
            <Link
              to='/libra'
              target='_blank'
              rel='noreferrer'
              search={createLibraSearch({
                orgId: organizationId,
                workflowId: source.workflowId,
                ticket: source.ticketId,
              })}>
              {source.title}
            </Link>
          ),
        },
        { key: 'version', label: 'Source version', children: source.version },
        { key: 'id', label: 'Reference', children: source.ticketId },
      ]}
    />
    {zones.length > 0 ? (
      <Table
        rowKey='id'
        dataSource={zones}
        columns={buildColumns(coverageByZone)}
        pagination={false}
        size='small'
      />
    ) : (
      <Alert
        type='warning'
        showIcon
        title='No zones were captured from this NII version'
        description={`This plan references assessment v${source.version} (${source.ticketId}). If zones were saved in another assessment version, create a plan from that completed ticket.`}
      />
    )}
  </>
)
